import React, { useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import * as yup from 'yup';

import emailSent from '../../assets/animations/email_sent.json';
import { usePageView } from '../../hooks/useTracking';
import { requestPasswordReset } from '../../model/UserAuthorization';
import Tracking from '../../services/Tracking';
import { TextInput } from '../Input/Input';
import { SubmitButton } from '../Input/SubmitButton';
import { useValidatedField } from '../Input/useField';
import { useForm } from '../Input/useForm';
import MobileModal from '../MobileModal';
import T from '../Translate';

function PasswordResetRequestSuccess({ onClose }) {
  usePageView('password_reset_sent');

  return (
    <MobileModal onClose={onClose} opened size="md">
      <div className="modal-body">
        <T as="h2" className="mt-2 mb-4" id="modals.forgottenPassword.success.title" />
        <Player autoplay controls={false} keepLastFrame loop={false} src={emailSent} />
        <T as="p" className="mt-2 mb-5" id="modals.forgottenPassword.success.description" />

        <button className="btn btn-primary btn-block" onClick={onClose} type="button">
          <T id="modals.forgottenPassword.success.submit" />
        </button>
      </div>
    </MobileModal>
  );
}

interface Props {
  onClose?: () => void;
}

export default function ForgottenPasswordModal({ onClose }: Props) {
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useValidatedField(
    'email',
    '',
    yup.string().email('global.email.invalid').required('global.required'),
  );

  const [handleSubmit, form] = useForm([email], async () => {
    try {
      Tracking.track('PASSWORD_RESET_SUBMITTED');
      await requestPasswordReset(email.value);
      setSuccess(true);
    } catch {
      // ignore
    }
  });

  usePageView('password_reset');

  if (success) {
    return <PasswordResetRequestSuccess onClose={onClose} />;
  }

  return (
    <MobileModal onClose={onClose} opened size="md">
      <form className="modal-body" onSubmit={handleSubmit}>
        <T as="h1" className="h4" id="modals.forgottenPassword.title" />
        <T as="p" className="sm mb-3" id="modals.forgottenPassword.description" />
        <TextInput
          autoComplete="email"
          autoFocus
          className="mb-5"
          error={form.errors.email}
          id="password-reset-email"
          label="modals.forgottenPassword.email.label"
          name="email"
          onChange={setEmail}
          type="email"
          value={email.value}
        />

        <SubmitButton block primary submitting={form.submitting}>
          <T id="modals.forgottenPassword.submit" />
        </SubmitButton>
      </form>
    </MobileModal>
  );
}
